import { isGlobalSurface } from '@/lib/surface-mode';
import type { Operator } from '@/types';

/** WorkOS org id baked into this market's Hub build (undefined in local/dev and on global). */
export function getMarketOrganizationId(): string | undefined {
  if (isGlobalSurface()) return undefined;
  const value = import.meta.env.VITE_WORKOS_ORGANIZATION_ID;
  return value && value.length > 0 ? value : undefined;
}

export function isSessionAlignedWithMarket(sessionOrganizationId: string | null | undefined): boolean {
  const marketOrganizationId = getMarketOrganizationId();
  if (!marketOrganizationId) return true;
  if (!sessionOrganizationId) return false;
  return sessionOrganizationId === marketOrganizationId;
}

/**
 * Pick the Hub operator for the active WorkOS session.
 * Market-pinned and global builds never fall back to operators[0] when the
 * session org does not match — that caused cross-market mis-selection.
 * Local/dev unpinned Hub still uses operators[0] as a last resort.
 */
export function resolveOperatorForSession(
  operators: Operator[],
  sessionOrganizationId: string,
  marketOrganizationId: string | undefined = getMarketOrganizationId()
): Operator | null {
  if (marketOrganizationId) {
    if (sessionOrganizationId !== marketOrganizationId) {
      return null;
    }
    return operators.find((operator) => operator.workos_organization_id === marketOrganizationId) ?? null;
  }

  const sessionMatch = operators.find((operator) => operator.workos_organization_id === sessionOrganizationId);
  if (sessionMatch) return sessionMatch;

  if (isGlobalSurface()) return null;
  return operators[0] ?? null;
}
